// fe-ios12/src/components/AppShareCard.js
// Admin tool: QR code + WhatsApp link to share the Saadhak Portal with patients,
// plus kiosk app install buttons that each open as a distinct PWA on the device.
import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const BASE = 'https://medicalyog.com';
const APP_URL = BASE + '/SaadhakPortal';

const WA_TEXT = encodeURIComponent(
  '\u{1F64F} *Medical Yoga Centre — Saadhak Portal*\n\n' +
  'Namaste! Your personal wellness dashboard is ready.\n\n' +
  '\u{1F517} ' + APP_URL + '\n\n' +
  '\u{1F4F1} *How to install as an App on your phone:*\n' +
  '\u2022 *iPhone/iPad (Safari)*: Tap Share \u2B06\uFE0F \u2192 "Add to Home Screen"\n' +
  '\u2022 *Android (Chrome)*: Tap "Install" when prompted, or tap \u22EE \u2192 "Add to Home Screen"\n\n' +
  'Once installed, the MYC icon will appear on your home screen for quick access. \u{1F9D8}'
);

// Kiosk apps — each installs as a separate PWA thanks to unique manifest id + start_url
const KIOSK_APPS = [
  { key: 'register', label: 'Registration Kiosk', emoji: '\u{1F4CB}', color: '#0c4a6e', path: '/kiosk/register', desc: 'On-site patient registration' },
  { key: 'kit',      label: 'KIT Campaign',       emoji: '\u{1F4E8}', color: '#065f46', path: '/kiosk/kit',      desc: 'KIT drip messaging kiosk' },
  { key: 'task',     label: 'Task Kiosk',         emoji: '\u2705',    color: '#4c1d95', path: '/kiosk/task',     desc: 'Staff checklist & task kiosk' },
  { key: 'expenses', label: 'Expenses',           emoji: '\u{1F4B0}', color: '#78350f', path: '/kiosk/expenses', desc: 'Expense viewer kiosk' },
];


export default function AppShareCard() {
  const [copied, setCopied] = useState(false);
  const [copiedKiosk, setCopiedKiosk] = useState('');
  const [installingKey, setInstallingKey] = useState('');

  function handleCopy() {
    navigator.clipboard.writeText(APP_URL).then(function () {
      setCopied(true);
      setTimeout(function () { setCopied(false); }, 2500);
    }).catch(function () {
      var ta = document.createElement('textarea');
      ta.value = APP_URL;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(function () { setCopied(false); }, 2500);
    });
  }

  function copyKioskUrl(app) {
    const url = BASE + app.path;
    navigator.clipboard.writeText(url).catch(function () {});
    setCopiedKiosk(app.key);
    setTimeout(function () { setCopiedKiosk(''); }, 2000);
  }

  function handleInstall(app) {
    // Open the kiosk URL — the kiosk page has its own manifest + service worker,
    // so the browser will trigger install prompt (Android) or the kiosk shows
    // "Add to Home Screen" instructions (iOS).
    const url = BASE + app.path;
    window.open(url, '_blank');
    setInstallingKey(app.key);
    setTimeout(function () { setInstallingKey(''); }, 3000);
  }

  return (
    <div style={{ maxWidth: 540, margin: '0 auto', padding: '24px 16px' }}>
      <style>{`
        .asc-card {
          background: #1e293b;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 18px;
          overflow: hidden;
        }
        .asc-qr-wrap {
          background: #fff;
          display: flex; align-items: center; justify-content: center;
          padding: 28px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .asc-action-btn {
          display: flex; align-items: center; justify-content: center; gap: 10px;
          width: 100%;
          border: none; border-radius: 12px;
          font-size: 15px; font-weight: 700;
          padding: 14px;
          cursor: pointer;
          transition: opacity 0.15s;
        }
        .asc-action-btn:hover { opacity: 0.88; }
        .asc-url-row {
          display: flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 10px;
          padding: 10px 12px;
          margin-bottom: 12px;
        }
        .asc-kiosk-card {
          display: flex; align-items: flex-start; gap: 14px;
          padding: 16px 16px;
          border-radius: 14px;
          border: 1px solid rgba(255,255,255,0.10);
          cursor: pointer;
          transition: background 0.15s, transform 0.1s;
        }
        .asc-kiosk-card:hover { transform: translateY(-1px); }
        .asc-kiosk-card:active { transform: scale(0.98); }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, color: '#f1f5f9', fontSize: 20, fontWeight: 800 }}>
          Share App with Patients
        </h2>
        <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 13 }}>
          Send this QR code or WhatsApp message so patients can install the MYC app on their phone.
        </p>
      </div>

      <div className="asc-card">
        {/* QR Code */}
        <div className="asc-qr-wrap">
          <div style={{ textAlign: 'center' }}>
            <QRCodeSVG
              value={APP_URL}
              size={200}
              level="H"
              includeMargin={true}
              imageSettings={{
                src: '/myc.png',
                x: undefined, y: undefined,
                height: 38, width: 38,
                excavate: true,
              }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', fontWeight: 600 }}>
              Scan to open Saadhak Portal
            </div>
          </div>
        </div>

        {/* URL + copy */}
        <div style={{ padding: '20px 20px 24px' }}>
          <div style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, marginBottom: 8, letterSpacing: 0.5 }}>
            DIRECT LINK
          </div>
          <div className="asc-url-row">
            <span style={{ flex: 1, color: '#38bdf8', fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>
              {APP_URL}
            </span>
            <button
              onClick={handleCopy}
              style={{
                background: copied ? 'rgba(34,197,94,0.15)' : 'rgba(56,189,248,0.12)',
                border: copied ? '1px solid rgba(34,197,94,0.30)' : '1px solid rgba(56,189,248,0.25)',
                borderRadius: 8, color: copied ? '#4ade80' : '#38bdf8',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                padding: '5px 12px', whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              {copied ? '\u2713 Copied!' : 'Copy'}
            </button>
          </div>

          {/* WhatsApp button */}
          <a
            href={`https://wa.me/?text=${WA_TEXT}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', display: 'block', marginBottom: 12 }}
          >
            <button
              className="asc-action-btn"
              style={{ background: '#25D366', color: '#fff' }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Send via WhatsApp
            </button>
          </a>

          {/* Instructions preview */}
          <div style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 10, padding: 14,
          }}>
            <div style={{ color: '#64748b', fontSize: 11, fontWeight: 700, marginBottom: 8, letterSpacing: 0.5 }}>
              MESSAGE PREVIEW
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
              {'\u{1F64F} Medical Yoga Centre \u2014 Saadhak Portal\n\nNamaste! Your personal wellness dashboard is ready.\n\n\u{1F517} ' + APP_URL + '\n\n\u{1F4F1} How to install:\n\u2022 iPhone/iPad: Tap Share \u2B06\uFE0F \u2192 "Add to Home Screen"\n\u2022 Android: Tap "Install" when prompted'}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Kiosk Apps Section ─── */}
      <div style={{
        marginTop: 28, background: '#1e293b', borderRadius: 18, padding: '24px 20px',
        border: '1px solid rgba(255,255,255,0.10)',
      }}>
        <h3 style={{ margin: '0 0 6px', color: '#f1f5f9', fontSize: 17, fontWeight: 800 }}>
          Install Kiosk Apps
        </h3>
        <p style={{ margin: '0 0 16px', color: '#94a3b8', fontSize: 13 }}>
          Tap <strong style={{ color: '#e2e8f0' }}>Install App</strong> to add each kiosk as a separate app on your device.
        </p>
        <div style={{ display: 'grid', gap: 10 }}>
          {KIOSK_APPS.map(app => {
            const url = BASE + app.path;
            const isCopied = copiedKiosk === app.key;
            return (
              <div
                key={app.key}
                className="asc-kiosk-card"
                style={{ background: app.color + '22', borderColor: app.color + '44' }}
                onClick={() => {}}
              >
                <span style={{
                  width: 50, height: 50, borderRadius: 14,
                  background: app.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 24, flexShrink: 0, marginTop: 2,
                  boxShadow: `0 2px 8px ${app.color}66`,
                }}>
                  {app.emoji}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: '#ffffff', letterSpacing: 0.3 }}>
                    {app.label}
                  </div>
                  <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 3, fontWeight: 500 }}>
                    {app.desc}
                  </div>
                  <div style={{ fontSize: 10, color: '#64748b', marginTop: 3, wordBreak: 'break-all' }}>
                    {url}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    <button
                      onClick={e => { e.stopPropagation(); window.open(url, '_blank'); }}
                      style={{
                        background: 'rgba(255,255,255,0.10)',
                        border: '1px solid rgba(255,255,255,0.20)',
                        borderRadius: 10, color: '#e2e8f0',
                        fontSize: 13, fontWeight: 700, padding: '8px 18px', cursor: 'pointer',
                      }}
                    >
                      Open
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); handleInstall(app); }}
                      style={{
                        background: app.color, border: 'none',
                        borderRadius: 10, color: '#fff',
                        fontSize: 13, fontWeight: 700, padding: '8px 18px', cursor: 'pointer',
                        boxShadow: '0 2px 8px ' + app.color + '88',
                      }}
                    >
                      {installingKey === app.key ? '\u2713 Installing...' : 'Install App'}
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); copyKioskUrl(app); }}
                      style={{
                        background: isCopied ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.06)',
                        border: '1px solid ' + (isCopied ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.12)'),
                        borderRadius: 10, color: isCopied ? '#4ade80' : '#94a3b8',
                        fontSize: 12, fontWeight: 600, padding: '8px 14px', cursor: 'pointer',
                      }}
                    >
                      {isCopied ? '\u2713 Copied' : 'Copy Link'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Install instructions */}
        <div style={{
          marginTop: 16, padding: 14,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 10,
        }}>
          <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, marginBottom: 6, letterSpacing: 0.5 }}>
            HOW TO INSTALL AS SEPARATE APPS
          </div>
          <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.8 }}>
            1. Tap <strong style={{ color: '#f1f5f9' }}>"Install App"</strong> above for the kiosk you want.<br />
            2. <strong style={{ color: '#f1f5f9' }}>Android (Chrome):</strong> Tap "Install" if prompted, or tap the 3-dot menu {'>'} "Add to Home Screen".<br />
            3. <strong style={{ color: '#f1f5f9' }}>iPhone / iPad (Safari):</strong> Tap the Share button (square with arrow) {'>'} "Add to Home Screen".<br />
            4. Each kiosk installs as its <strong style={{ color: '#f1f5f9' }}>own separate app</strong> with a unique icon and color.
          </div>
        </div>
      </div>
    </div>
  );
}
